import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from '@reach/router';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Link } from 'gatsby';
import DriverLogo from '/src/images/driver-logo.svg';

const THRESHOLD = 50;

const PAGES = [{
	labelKey: 'what-is-driver',
	path: '/dash-cam',
	children: [{
		labelKey: 'dash-cam',
		path: '/dash-cam',
	}, {
		labelKey: 'intelligent-safety',
		path: '/intelligent-safety',
	}, {
		labelKey: 'science-in-society',
		path: '/science-in-society',
	}],
}, {
	labelKey: 'for-business',
	path: '/driver-pro',
}, {
	labelKey: 'for-education',
	path: '/driver-edu',
}, {
	labelKey: 'for-trucking',
	path: '/dti-trucking',
}];

const NavLink = ({ labelKey, path, children }) => {
	const { t } = useTranslation(null, { keyPrefix: 'header' });
	const { pathname } = useLocation();

	const isChildSelected = useMemo(() => {
		if (!Array.isArray(children)) return false;

		return children.some(child => {
			return pathname.startsWith(child.path);
		});
	}, [pathname, children]);

	const isSelected = useMemo(() => {
		return pathname.startsWith(path);
	}, [pathname, path]);

	const classes = useMemo(() => {
		return classNames({
			selected: isSelected,
			'child-selected': isChildSelected,
		}, 'semibold');
	}, [isSelected, isChildSelected]);

	return (
		<li className={classes}>
			<Link to={path}>{t(labelKey)}</Link>

			{Array.isArray(children) ? (
				<ul>
					{children.map(child => (
						<NavLink
							key={`nav-link-${child.labelKey}`}
							{...child}
						/>
					))}
				</ul>
			) : null}
		</li>
	);
};

const NavHeader = ({ dark }) => {
	const { t } = useTranslation(null, { keyPrefix: 'header' });

	const [isOpen, setIsOpen] = useState(false);
	const [isSticking, setIsSticking] = useState(false);

	const onScroll = useCallback(() => {
		const y = window.pageYOffset;

		if (y >= THRESHOLD && !isSticking) {
			setIsSticking(true);
		} else if (y < THRESHOLD && isSticking) {
			setIsSticking(false);
		}
	}, [isSticking]);

	useEffect(() => {
		window.addEventListener('scroll', onScroll);

		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	}, [onScroll]);

	const toggleMenuOpen = useCallback(() => {
		setIsOpen(!isOpen);
	}, [isOpen]);

	const classes = useMemo(() => {
		return classNames({
			dark,
			open: isOpen,
			sticking: isSticking,
		});
	}, [isSticking, isOpen, dark]);

	return (
		<header id="driver-nav-header" className={classes}>
			<div>
				{/*<div className="spacer" />*/}

				<Link className="logo" to="/">
					<img
						src={DriverLogo}
						alt={t('driver-technologies')}
					/>
				</Link>

				<div
					className="hamburger"
					role="button"
					tabIndex="0"
					onClick={toggleMenuOpen}
					onKeyDown={toggleMenuOpen}
					aria-label="Menu">
					<div />
				</div>

				<div className="menu">
					<nav>
						<ul>
							{PAGES.map(page => (
								<NavLink
									key={`nav-link-${page.labelKey}`}
									{...page}
								/>
							))}
						</ul>
					</nav>

					<a 
						href="https://cloud.drivertechnologies.com" 
						target="_blank"
						rel="noreferrer">
						<button>{t('login-to-driver-cloud')}</button>
					</a>			
				</div>

				{/*<div className="spacer" />*/}
			</div>
		</header>
	);
};

NavHeader.defaultProps = {
	dark: false,
};

export default NavHeader;
